// ============================================================================
// FSA CLASSIFICATION HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { fsaClassificationService } from 'api/services/fsa/fsaClassificationService';
import type { ID } from 'api/types';
import type { CoverageSummary, AutoClassifySuggestion } from 'api/types/fsa/classification.types';

export const useCoverage = (trialBalanceId: ID | null | undefined) => {
  const [data, setData] = useState<CoverageSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!trialBalanceId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fsaClassificationService.getCoverage(trialBalanceId);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [trialBalanceId]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, loading, error, refetch };
};

export const useAutoClassifySuggestions = (trialBalanceId: ID | null | undefined) => {
  const [data, setData] = useState<AutoClassifySuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!trialBalanceId) {
      setData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fsaClassificationService.getSuggestions(trialBalanceId);
      setData(response.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [trialBalanceId]);

  useEffect(() => { refetch(); }, [refetch]);

  return { data, loading, error, refetch };
};
